// Text catalog for the Ydelser / Services overview page.
// Card images and links live in src/pages/YdelserPage.tsx and are aligned by
// index with the cards array here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type ServiceCard = { title: string; text: string };

type YdelserStrings = {
  meta: PageMeta;
  jsonLd: { serviceType: string };
  hero: { eyebrow: string; title: string; body: string };
  section: { heading: string; intro: string };
  readMore: string;
  cards: ServiceCard[];
  cta: { heading: string; body: string; button: string };
};

export const ydelserStrings: Record<Locale, YdelserStrings> = {
  da: {
    meta: {
      title: "Ydelser - Konsulenter til byggeprojekter - MI Support",
      description:
        "MI Support tilbyder byggeledelse, projektledelse og sikkerhedsledelse (HSE) til større, komplekse byggeprojekter.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    jsonLd: { serviceType: "Konsulentydelser til byggeprojekter" },
    hero: {
      eyebrow: "Vores konsulentydelser",
      title: "Ydelser",
      body: "MI Support er en moderne og uafhængig konsulent-, ingeniør- og rådgivningsvirksomhed med mere end 25 års erfaring i byggebranchen. Vi tilbyder skræddersyede konsulentydelser inden for bygge-, projekt- og sikkerhedsledelse.",
    },
    section: {
      heading: "Vores ydelser",
      intro:
        "Vi tilbyder et bredt udvalg af konsulentydelser inden for bygge-, projekt- og sikkerhedsledelse. Vores erfarne konsulenter er klar til at hjælpe jer gennem hele byggeprocessen.",
    },
    readMore: "Læs mere",
    cards: [
      {
        title: "Projektledelse",
        text: "Erfarne projektledere skaber fremdrift, koordinerer aktiviteter og håndterer risici, så I styrer projektet sikkert i mål med færre forsinkelser og fuldt overblik.",
      },
      {
        title: "Byggeledelse",
        text: "Vores byggeledere tager projektet fra idé til virkelighed og planlægger og styrer jeres næste bygge- eller anlægsprojekt med stærke kompetencer og bred erfaring.",
      },
      {
        title: "Bygherrerådgivning",
        text: "Som uafhængig bygherrerådgiver varetager vi bygherrens interesser fra strategi og udbud til ibrugtagning, med styr på økonomi, tid, kvalitet, risici og bæredygtighed.",
      },
      {
        title: "Sikkerhedsledelse (HSE)",
        text: "Sikkerhedskoordinatorer der forebygger ulykker og minimerer risici for skader og sygdom, så alle kommer trygt hjem fra pladsen hver dag.",
      },
      {
        title: "Tidsplanlægning",
        text: "Robuste hovedtidsplaner og fremdriftsstyring i Primavera P6 og MS Project, så komplekse byggerier afleveres til tiden.",
      },
      {
        title: "Projektøkonomi",
        text: "Projektcontrollere tager hånd om økonomien fra estimat og budget til forecast, cost control og slutregnskab, med fuld transparens for bygherre og ledelse.",
      },
      {
        title: "Projektassistance",
        text: "Projektassistenter styrker organisationen med dokumentstyring, mødekoordinering og rapportering, så projektledelsen kan fokusere på fremdrift og beslutninger.",
      },
      {
        title: "Planlægning og ledelse af shutdowns",
        text: "Erfarne shutdown managers leder turnarounds og revisioner på industri- og produktionsanlæg, med minimal nedetid og fuld kontrol over scope, tid og sikkerhed.",
      },
      {
        title: "SiteSupport",
        text: "Vores digitale platform til byggepladsen, der samler dokumentation, registreringer og kommunikation ét sted for projektledelse, byggeledelse og HSE.",
      },
    ],
    cta: {
      heading: "Skal vi starte et nyt samarbejde?",
      body: "MI Support står klar til at hjælpe jer med jeres næste projekt, uanset om det er en mindre byggeopgave eller et større prestigefuldt byggeprojekt.",
      button: "Kontakt os og hør mere",
    },
  },

  en: {
    meta: {
      title: "Services - Consultants for construction - MI Support",
      description:
        "MI Support offers construction management, project management and safety management (HSE) for larger, complex construction projects.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    jsonLd: { serviceType: "Consultancy services for construction projects" },
    hero: {
      eyebrow: "Our consultancy services",
      title: "Services",
      body: "MI Support is a modern and independent consultancy, engineering and advisory company with more than 25 years of experience in the construction industry. We offer tailored consultancy services within construction, project and safety management.",
    },
    section: {
      heading: "Our services",
      intro:
        "We offer a broad range of consultancy services within construction, project and safety management. Our experienced consultants are ready to help you through the entire construction process.",
    },
    readMore: "Read more",
    cards: [
      {
        title: "Project management",
        text: "Experienced project managers create momentum, coordinate activities and manage risks, so you steer the project safely to the finish with fewer delays and full overview.",
      },
      {
        title: "Construction management",
        text: "Our construction managers take the project from idea to reality and plan and manage your next construction or civil engineering project with strong skills and broad experience.",
      },
      {
        title: "Client consultancy",
        text: "As an independent client advisor, we safeguard the client's interests from strategy and tender to commissioning, keeping control of economy, time, quality, risks and sustainability.",
      },
      {
        title: "Safety management (HSE)",
        text: "Safety coordinators who prevent accidents and minimise the risk of injury and illness, so everyone gets home safely from the site every day.",
      },
      {
        title: "Scheduling",
        text: "Robust master schedules and progress management in Primavera P6 and MS Project, so complex builds are delivered on time.",
      },
      {
        title: "Project economics",
        text: "Project controllers take care of the economy from estimate and budget to forecast, cost control and final accounts, with full transparency for client and management.",
      },
      {
        title: "Project assistance",
        text: "Project assistants strengthen the organisation with document management, meeting coordination and reporting, so project management can focus on momentum and decisions.",
      },
      {
        title: "Shutdown planning and management",
        text: "Experienced shutdown managers lead turnarounds and overhauls at industrial and production facilities, with minimal downtime and full control of scope, time and safety.",
      },
      {
        title: "SiteSupport",
        text: "Our digital platform for the construction site, gathering documentation, registrations and communication in one place for project management, construction management and HSE.",
      },
    ],
    cta: {
      heading: "Shall we start a new collaboration?",
      body: "MI Support is ready to help you with your next project, whether it is a smaller construction task or a larger, prestigious construction project.",
      button: "Contact us to learn more",
    },
  },

  de: {
    meta: {
      title: "Leistungen - Berater für Bauprojekte - MI Support",
      description:
        "MI Support bietet Baumanagement, Projektleitung und Sicherheitsmanagement (HSE) für größere, komplexe Bauprojekte.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    jsonLd: { serviceType: "Beratungsleistungen für Bauprojekte" },
    hero: {
      eyebrow: "Unsere Beratungsleistungen",
      title: "Leistungen",
      body: "MI Support ist ein modernes und unabhängiges Beratungs-, Ingenieur- und Consultingunternehmen mit mehr als 25 Jahren Erfahrung in der Baubranche. Wir bieten maßgeschneiderte Beratungsleistungen in den Bereichen Bau-, Projekt- und Sicherheitsmanagement.",
    },
    section: {
      heading: "Unsere Leistungen",
      intro:
        "Wir bieten ein breites Spektrum an Beratungsleistungen in den Bereichen Bau-, Projekt- und Sicherheitsmanagement. Unsere erfahrenen Berater stehen bereit, Ihnen durch den gesamten Bauprozess zu helfen.",
    },
    readMore: "Mehr erfahren",
    cards: [
      {
        title: "Projektleitung",
        text: "Erfahrene Projektleiter schaffen Fortschritt, koordinieren Aktivitäten und steuern Risiken, sodass Sie das Projekt sicher ans Ziel bringen – mit weniger Verzögerungen und vollem Überblick.",
      },
      {
        title: "Baumanagement",
        text: "Unsere Bauleiter bringen das Projekt von der Idee zur Wirklichkeit und planen und steuern Ihr nächstes Bau- oder Anlagenprojekt mit starken Kompetenzen und breiter Erfahrung.",
      },
      {
        title: "Bauherrenberatung",
        text: "Als unabhängiger Bauherrenberater wahren wir die Interessen des Bauherrn von Strategie und Ausschreibung bis zur Inbetriebnahme – mit Kontrolle über Wirtschaftlichkeit, Zeit, Qualität, Risiken und Nachhaltigkeit.",
      },
      {
        title: "Sicherheitsmanagement (HSE)",
        text: "Sicherheitskoordinatoren, die Unfälle verhindern und das Risiko von Verletzungen und Krankheiten minimieren, sodass jeden Tag alle sicher von der Baustelle nach Hause kommen.",
      },
      {
        title: "Terminplanung",
        text: "Robuste Hauptterminpläne und Fortschrittssteuerung in Primavera P6 und MS Project, sodass komplexe Bauten termingerecht übergeben werden.",
      },
      {
        title: "Projektökonomie",
        text: "Projektcontroller kümmern sich um die Wirtschaftlichkeit – von Schätzung und Budget über Forecast und Cost Control bis zur Schlussabrechnung, mit voller Transparenz für Bauherr und Leitung.",
      },
      {
        title: "Projektassistenz",
        text: "Projektassistenten stärken die Organisation mit Dokumentenmanagement, Besprechungskoordination und Berichtswesen, sodass sich die Projektleitung auf Fortschritt und Entscheidungen konzentrieren kann.",
      },
      {
        title: "Planung und Leitung von Shutdowns",
        text: "Erfahrene Shutdown-Manager leiten Turnarounds und Revisionen an Industrie- und Produktionsanlagen, mit minimaler Ausfallzeit und voller Kontrolle über Scope, Zeit und Sicherheit.",
      },
      {
        title: "SiteSupport",
        text: "Unsere digitale Plattform für die Baustelle, die Dokumentation, Registrierungen und Kommunikation an einem Ort bündelt – für Projektleitung, Bauleitung und HSE.",
      },
    ],
    cta: {
      heading: "Sollen wir eine neue Zusammenarbeit starten?",
      body: "MI Support steht bereit, Ihnen bei Ihrem nächsten Projekt zu helfen – ob es sich um eine kleinere Bauaufgabe oder ein größeres, prestigeträchtiges Bauprojekt handelt.",
      button: "Kontaktieren Sie uns für mehr Informationen",
    },
  },
};
